import React, {useEffect, useMemo, useRef} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';
import {
    BaseFormComponent,
    ConditionalRule,
    FormComponent,
    FormType,
    FormValues,
    SelectComponent
} from "./DynamicFormTypes.ts";
import {
    collectDescendantFields,
    defaultParserForSelects,
    extractDataGridKey,
    extractSchemaFieldKey
} from "./dyanimcFormUtil.tsx";
import {useFormInfo, useFormMethods, useFormStatic} from "./DyanmicFormContext.tsx";
import {getDefaultValue} from "./FormRenderer.tsx";
import equal from "fast-deep-equal";

interface ConditionalWrapperProps {
    component: BaseFormComponent;
    children: React.ReactNode;
}

/**
 * Recursively extracts all unique fieldKeys mentioned in a ConditionalRule object.
 */
const extractConditionalFieldKeys = (
    rule: ConditionalRule | undefined,
    keys: Set<string> = new Set()
): Set<string> => {
    if (!rule) {
        return keys;
    }

    if (rule.when) {
        keys.add(rule.when);
    }

    if (rule.and) {
        rule.and.forEach(subRule => extractConditionalFieldKeys(subRule, keys));
    }

    if (rule.or) {
        rule.or.forEach(subRule => extractConditionalFieldKeys(subRule, keys));
    }

    if (rule.not) {
        extractConditionalFieldKeys(rule.not, keys);
    }

    return keys;
};

const ConditionalWrapperRenderer: React.FC<ConditionalWrapperProps> = ({
                                                                          component,
                                                                          children
                                                                      }) => {
    const { formMethods } = useFormMethods()
    const { flattenedSchemaMap, metaData } = useFormStatic();
    const { setHidden, removeHidden } = useFormInfo();

    const isVisible = useConditional({
        conditional: component.conditional!,
        component,
        formMethods,
        flattenedSchemaMap,
        metaData,
        setHidden,
        removeHidden,
    });

    if (!isVisible) {
        return null;
    }

    return <>{children}</>;
}

export const ConditionalWrapper = React.memo(ConditionalWrapperRenderer);

// ===== Hook =====

interface UseConditionalOptions {
    conditional: ConditionalRule;
    component: BaseFormComponent;
    formMethods: UseFormReturn<FormValues>;
    flattenedSchemaMap: Map<string, FormComponent>;
    metaData: any;
    setHidden: (parentFieldKey: string) => void;
    removeHidden: (parentFieldKey: string) => void;
}

const useConditional = ({
    conditional,
    component,
    formMethods,
    flattenedSchemaMap,
    metaData,
    setHidden,
    removeHidden,
}: UseConditionalOptions): boolean => {
    const { control } = formMethods;

    // Memoize the extraction of all relevant field keys from the conditional rule.
    const allRelevantFieldKeys = useMemo(() => {
        return Array.from(extractConditionalFieldKeys(conditional));
    }, [conditional]);

    // Use useWatch to subscribe only to the specific fields identified as relevant.
    const rawWatchedValues = useWatch({
        control,
        name: allRelevantFieldKeys,
        disabled: allRelevantFieldKeys.length === 0
    });

    // Stabilize the watched values - useWatch returns a new array reference every render
    // even when values haven't changed, which would defeat useMemo below.
    const stableWatchedRef = useRef(rawWatchedValues);
    if (!equal(stableWatchedRef.current, rawWatchedValues)) {
        stableWatchedRef.current = rawWatchedValues;
    }
    const watchedRelevantValues = stableWatchedRef.current;

    const needsWarning = !!conditional.warningMessage;
    const canClearFields = !conditional.dontClearFields;

    // --- Evaluate current visibility (memoized - only recalculates when watched values actually change) ---
    const isVisible = useMemo(() => {
        const getFieldValue = (fieldKey: string) => {
            const index = allRelevantFieldKeys.indexOf(fieldKey);
            const value = index !== -1 ? watchedRelevantValues[index] : undefined;
            return defaultParserForSelects(value);
        };

        const evaluate = (rule: ConditionalRule): boolean => {
            if (rule.and && rule.and.length > 0) {
                return rule.and.every(subcondition => evaluate(subcondition));
            }
            if (rule.or && rule.or.length > 0) {
                return rule.or.some(subcondition => evaluate(subcondition));
            }
            if (rule.not) {
                return !evaluate(rule.not);
            }
            if (rule.when) {
                const fieldValue = getFieldValue(rule.when);
                return evaluateFieldCondition(rule, fieldValue, flattenedSchemaMap);
            }
            return true;
        };

        return evaluate(conditional)
    }, [watchedRelevantValues, allRelevantFieldKeys, conditional, flattenedSchemaMap]);

    // --- Transition detection & confirmation ---

    // Track previous visibility (null = initial render, hasn't been set yet)
    const prevVisibleRef = useRef<boolean | null>(null);

    // Track previous trigger field values so we can revert on cancel
    const prevTriggerValuesRef = useRef<{ key: string; value: any }[]>([]);

    // Snapshot trigger field values while visible (so we can revert if user cancels hide)
    useEffect(() => {
        if (isVisible && needsWarning) {
            prevTriggerValuesRef.current = allRelevantFieldKeys.map(key => ({
                key,
                value: structuredClone(formMethods.getValues(key))
            }));
        }
    }, [isVisible, allRelevantFieldKeys, formMethods, watchedRelevantValues]);

    // Handle visibility transitions
    useEffect(() => {
        // Initial render - just record the state
        if (prevVisibleRef.current === null) {
            prevVisibleRef.current = isVisible;
            if (!isVisible) {
                setHidden(component.fieldKey);
            }
            return;
        }

        // Visible -> Hidden transition
        if (prevVisibleRef.current && !isVisible) {
            //if it doesnt need a warning, clear the fields, otherwise, wait for confirmation
            if (canClearFields) {
                if (!needsWarning || (needsWarning && window.confirm(conditional.warningMessage))) {
                    let descendantFields = collectDescendantFields(component);
                    // reset each descendant field to its schema default
                    console.log(`${component.fieldKey} is being set to hidden so we will clear`, descendantFields)
                    descendantFields.forEach(({ fieldKey }) => {
                        const key = extractDataGridKey(fieldKey);
                        const schemaComponent = flattenedSchemaMap.get(key.fieldKey);
                        if (schemaComponent) {
                            const defaultVal = getDefaultValue(schemaComponent, metaData);
                            formMethods.resetField(fieldKey, { defaultValue: defaultVal });
                        }
                    });
                    setHidden(component.fieldKey);
                    prevVisibleRef.current = false;
                }
                else {
                    // Cancelled - revert trigger fields to their previous values
                    prevTriggerValuesRef.current.forEach(({ key, value }) => {
                        formMethods.setValue(key, value);
                    });
                    // prevVisibleRef stays true since we reverted
                }
            }
            else {
                setHidden(component.fieldKey);
            }

            return;
        }

        // Hidden -> Visible transition
        if (!prevVisibleRef.current && isVisible) {
            removeHidden(component.fieldKey);
            prevVisibleRef.current = true;
        }
    }, [isVisible]);

    return isVisible;
};


// ===== Field condition evaluation (pure) =====

const evaluateFieldCondition = (
    condition: ConditionalRule,
    fieldValue: any,
    flattenedSchemaMap: Map<string, FormComponent>
): boolean => {
    // Equal comparison (original behavior)
    if ('eq' in condition) {
        if (Array.isArray(fieldValue)) {
            return fieldValue.includes(condition.eq);
        }
        if (fieldValue && typeof fieldValue === "object") {
            return fieldValue.value === condition.eq;
        }
        return fieldValue === condition.eq;
    }

    // Not equal comparison
    if ('notEq' in condition) {
        if (fieldValue == undefined) {
            return true;
        }
        if (Array.isArray(fieldValue)) {
            return !fieldValue.includes(condition.notEq);
        }
        if (fieldValue && typeof fieldValue === "object") {
            return fieldValue.value !== condition.notEq;
        }
        return fieldValue !== condition.notEq;
    }

    // Empty check
    if (condition.isEmpty === true) {
        if (fieldValue === undefined || fieldValue === null) return true;
        if (Array.isArray(fieldValue)) return fieldValue.length === 0;
        if (typeof fieldValue === 'object') return Object.keys(fieldValue).length === 0;
        if (typeof fieldValue === 'string') return fieldValue.trim() === '';
        return false;
    }

    // Not empty check
    if (condition.notEmpty === true) {
        if (fieldValue === undefined || fieldValue === null) return false;
        if (Array.isArray(fieldValue)) return fieldValue.length > 0;
        if (typeof fieldValue === 'object') return Object.keys(fieldValue).length > 0;
        if (typeof fieldValue === 'string') return fieldValue.trim() !== '';
        return true;
    }

    // Numeric comparisons
    if (typeof fieldValue === 'number' || !isNaN(Number(fieldValue))) {
        const numValue = Number(fieldValue);

        if ('greaterThan' in condition && typeof condition.greaterThan === 'number') {
            return numValue > condition.greaterThan;
        }

        if ('lessThan' in condition && typeof condition.lessThan === 'number') {
            return numValue < condition.lessThan;
        }

        if ('greaterThanOrEqual' in condition && typeof condition.greaterThanOrEqual === 'number') {
            return numValue >= condition.greaterThanOrEqual;
        }

        if ('lessThanOrEqual' in condition && typeof condition.lessThanOrEqual === 'number') {
            return numValue <= condition.lessThanOrEqual;
        }
    }

    // String operations
    if (typeof fieldValue === 'string') {
        if ('contains' in condition && typeof condition.contains === 'string') {
            return fieldValue.includes(condition.contains);
        }

        if ('startsWith' in condition && typeof condition.startsWith === 'string') {
            return fieldValue.startsWith(condition.startsWith);
        }

        if ('endsWith' in condition && typeof condition.endsWith === 'string') {
            return fieldValue.endsWith(condition.endsWith);
        }
    }

    // Array operations
    if ('includesAny' in condition && Array.isArray(condition.includesAny) && condition.includesAny !== undefined) {
        if (fieldValue && typeof fieldValue === "object") {
            return condition.includesAny.some(item => fieldValue.value === item);
        }
    }

    if ('doesntIncludeAny' in condition && Array.isArray(condition.doesntIncludeAny) && condition.doesntIncludeAny !== undefined) {
        if (!fieldValue) {
            return true;
        }
        if (typeof fieldValue === "object" && "value" in fieldValue && condition.when) {
            const watchedComponent = flattenedSchemaMap.get(extractSchemaFieldKey(condition.when));
            if (watchedComponent && watchedComponent.type === FormType.SELECT) {
                const selectComponent = watchedComponent as SelectComponent;
                if (selectComponent && selectComponent.provided) {
                    const values = selectComponent.provided.map(value => value.value);
                    if (values && !values.includes(fieldValue.value)) {
                        return true;
                    }
                }
            }
            return !condition.doesntIncludeAny.some(item => fieldValue.value === item) || fieldValue.value === undefined;
        }
    }

    if ('includesAll' in condition && Array.isArray(condition.includesAll)) {
        return condition.includesAll.every(item => fieldValue === item);
    }

    return false; // Default to false if no condition matched
};
